import React from 'react';
import Seo from '../../components/Seo';
import NewNavbar from '../../components/NewNavbar';
import NewFooter from '../../components/NewFooter';
import TestimonialsSection from '../../components/HomePage/TestimonialsSection';
import TypedHeading from '../../components/TypedHeading';
import FaqAccordion from '../../components/FaqAccordion';
import PriceAccordion from '../../components/PriceAccordion';
import ContactForm, { CONTACT_FORM_CLASSES } from '../../components/ContactForm/ContactForm';
import './travelclinic.css';

/* ── Images ─────────────────────────────────────────────────── */
const HERO_BG = '/assets/new_assets/new-banner-sample.2d328565ab33db483d79.jpg';
const CTA_VIDEO = '/assets/new_assets/video-travel.mp4';

/* ── Data ───────────────────────────────────────────────────── */
const VACCINATIONS = [
  { name: 'Diphtheria-Tetanus-Polio (DTP) Vaccine', price: '£20' },
  { name: 'Typhoid Vaccine - Injection', price: '£25' },
  { name: 'Cholera Vaccine (Oral)', price: '£30' },
  { name: 'Typhoid Vaccine - Oral', price: '£30' },
  { name: 'Hepatitis B Vaccine - Adult', price: '£35' },
  { name: 'Hepatitis A Vaccine - Adult', price: '£40' },
  { name: 'Meningitis ACWY Vaccine', price: '£45' },
  { name: 'Hepatitis A and B Combined Vaccine - Adult', price: '£60' },
  { name: 'Rabies Vaccine', price: '£60' },
  { name: 'Tick-Borne Encephalitis (TBE) Vaccine', price: '£60' },
  { name: 'Yellow Fever Vaccine', price: '£60' },
  { name: 'Japanese Encephalitis (JE) Vaccine*', price: '£95' },
];

const FAQS = [
  {
    q: 'What vaccinations do I need before traveling?',
    a: (<>
      The vaccinations you need depend on your travel destination, the duration of your trip, and your medical history. Common vaccines include Hepatitis A, Hepatitis B, Typhoid, Yellow Fever, and Meningitis. Please consult with us to determine the specific vaccines required for your destination, or visit <a href="https://travelhealthpro.org.uk/countries" target="_blank" rel="noopener noreferrer">TravelHealthPro</a> for more information.
    </>),
  },
  {
    q: 'How far in advance should I get my travel vaccinations?',
    a: "It’s best to start your travel vaccination planning 4-6 weeks before your trip. This allows enough time for the vaccines to become effective and ensures a full course of multiple vaccines can be administered, if necessary. However, some vaccines can be administered sooner or at the last minute. Contact us if you are unsure.",
  },
  {
    q: 'Are Travel Vaccinations Covered By The NHS?',
    a: 'Some travel vaccinations are available free on the NHS, such as for Hepatitis A or DTP, if traveling to certain destinations. However, other vaccines like Yellow Fever or Japanese Encephalitis may require a private prescription or travel clinic. All our vaccinations are given privately.',
  },
  {
    q: 'What should I do if I miss a vaccine dose?',
    a: "If you miss a dose of your vaccine, contact us immediately. We’ll provide guidance on how to proceed and whether you need to reschedule your vaccination. Some vaccines can be completed even if doses are delayed.",
  },
  {
    q: 'Can I receive multiple vaccines at once?',
    a: "Yes, many travel vaccines can be given together in a single appointment. We’ll ensure the vaccines are safe to administer simultaneously based on your travel and medical history.",
  },
  {
    q: 'What is malaria, and how can I prevent it?',
    a: 'Malaria is a serious mosquito-borne illness common in tropical and subtropical regions. Prevention includes taking antimalarial medications before, during, and after your trip, as well as using insect repellent and sleeping under mosquito nets. We can provide advice based on your destination.',
  },
  {
    q: 'Are there any side effects from travel vaccinations?',
    a: 'Like any vaccine, there may be mild side effects, including pain or swelling at the injection site, low-grade fever, or tiredness. Severe side effects are rare. If you experience anything unusual, contact us immediately.',
  },
  {
    q: 'Do I need a Yellow Fever vaccination?',
    a: "A Yellow Fever vaccination is required if you’re traveling to certain countries in Africa and South America. It’s also necessary for entry into some countries if you’ve recently visited a Yellow Fever-endemic area. You will receive an International Certificate of Vaccination upon receiving the vaccine.",
  },
  {
    q: 'How do I know which vaccinations I need for my destination?',
    a: (<>
      Each destination has its own vaccination requirements. You can check with us or visit <a href="https://travelhealthpro.org.uk/countries" target="_blank" rel="noopener noreferrer">TravelHealthPro</a> for destination-specific health advice. We can guide you on the necessary vaccinations and health precautions for your travels.</>),
  },
];

const TESTIMONIALS = [
  {
    author: 'Vivien Le',
    quote: "Excellent spot for travel vaccinations with decent prices that you don't find elsewhere. The staff is very friendly and knowledgeable. Great location. Would definitely recommend.",
  },
  {
    author: 'Ian Bright',
    quote: 'Great experience. Professional and speedy. Got an appointment for the the next day and I was in and out of the building in 5 minutes including adding a vaccine that wasn\'t in my original booking.',
  },
  {
    author: 'Bessie Williams',
    quote: "I can't recommend this service enough! Sana was a delight to visit throughout my vaccinations, so quick and professional. It makes such a difference when pharmacists are so lovely, will definitely continue using this company in the future. Thanks so much again :)",
  },
  {
    author: 'Ross Palmer',
    quote: 'I got three travel vaccinations in total. All appointments were booked quickly and there were no issues whatsoever. Sana administered my vaccines and she was absolutely fantastic. Would recommend',
  },
  {
    author: 'Rose Cranmer',
    quote: 'I had a medical issue while traveling and was recommended to go to this clinic. My experience was amazing! I got seen right away and all the staff were lovely – Sana was amazing. Thank you!!',
  },
];

const TYPED_STRINGS = ['Vaccinations'];

function TravelClinicPage() {

  return (
    <>
      <Seo
        title="My Private Chemist | Travel Clinic"
        description="Book travel vaccinations and malaria prevention with our expert pharmacists. Fast appointments and personalised advice for your destination and trip."
        canonical={`${process.env.REACT_APP_BASE_URL}/travel-clinic`}
        url={`${process.env.REACT_APP_BASE_URL}/travel-clinic`}
        image={`${process.env.REACT_APP_BASE_URL}${HERO_BG}`}
      />
      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="tc-hero" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="tc-hero__navbar-wrap">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <NewNavbar />
              </div>
            </div>
          </div>
        </div>
        <div className="tc-hero__content">
          <h1 className="tc-hero__h1">Travel Clinic</h1>
          <nav className="tc-hero__breadcrumb" aria-label="Breadcrumb">
            <a href="/" className="tc-hero__bc-link">Home</a>
            <span className="tc-hero__bc-sep"> • </span>
            <span className="tc-hero__bc-current">Travel Clinic</span>
          </nav>
        </div>
      </section>

      <main>
        {/* ── Travel Vaccinations Intro ─────────────────────── */}
        <section className="tc-intro">
          <div className="tc-intro__container">
            <h4 className="tc-intro__heading">Travel Vaccinations</h4>
            <p className="tc-intro__text">
              We make it easy as we can for you to get the right vaccinations and travel health.
              During your consultation, our travel staff will check which vaccines and malaria
              prevention you need, this will depend on where you are going, which activities you
              have planned and your medical history.
            </p>
          </div>
        </section>

        {/* ── CTA Banner ───────────────────────────────────────── */}
        <section className="tc-cta" id="vaccination">
          <video
            className="tc-cta__video"
            src={CTA_VIDEO}
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="tc-cta__inner">
            <h2 className="tc-cta__h2">
              Wherever life takes you,&nbsp;&nbsp;your journey covered with care.
            </h2>
            <a
              href="#tc-contact"
              className="tc-cta__btn"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('tc-contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              BOOK APPOINTMENT
            </a>
          </div>

        </section>
        {/* Show Prices accordion */}
        <PriceAccordion
          sectionClassName="prices_list"
          containerClassName="tc-prices"
          barClassName="tc-prices__toggle"
          labelClosed="Show Prices"
          labelOpen="Hide Prices"
          iconClassName="tc-prices__chevron"
          iconOpenClass="tc-prices__chevron--up"
          iconContent={null}
          bodyClassName="tc-prices__content"
          tableClassName="tc-prices__table"
          useThead
          columns={['Vaccination', 'Price']}
          rows={VACCINATIONS.map(v => [v.name, v.price])}
          noteClassName="tc-prices__note"
          note="*Offer valid for appointments before 30th April"
        />

        {/* white gap between CTA and Search (115px, WP Elementor spacer) */}
        <div className="tc-cta-gap" />

        {/* ── Search by Vaccinations ──────────────────────────── */}
        <section className="tc-search">
          <div className="tc-search__inner">
            <h4 className="tc-search__heading">Search by&nbsp; <TypedHeading strings={TYPED_STRINGS} className="tc-search__heading-typed" /></h4>
            <p className="tc-search__body">
              Search by 'vaccinations by disease' and have them administered by us.
            </p>
            <p className="tc-search__body">
              If you're unsure which vaccinations you need for your travel, please visit{' '}
              <a
                href="https://travelhealthpro.org.uk/countries"
                target="_blank"
                rel="noopener noreferrer"
                className="tc-search__link"
              >
                TravelHealthPro
              </a>.
            </p>
            <div className="tc-search__cards">
              <div className="tc-search__card">
                <a
                  href="/travel-clinic/vaccinations-by-diseases"
                  className="tc-search__card-btn"
                >
                  VACCINATION BY DIsease
                </a>
                <p className="tc-search__card-text">
                  Explore our list of vaccines we offer. Add to cart and select an appointment
                  time for when you would like them administered.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Contact Form ─────────────────────────────────────── */}
        <section className="tc-contact" id="tc-contact">
          <div className="tc-contact__inner">
            <h4 className="tc-contact__heading">Still Unsure On What Vaccination You Need?</h4>
            <p className="tc-contact__body">
              Contact one of our healthcare staff to help you choose the right ones for you.
            </p>
            <a href="tel:02039879202" className="tc-contact__phone">
              <svg className="tc-contact__phone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.0 1.22 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.18 6.18l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
              0203 987 9202
            </a>
            <ContactForm
              idPrefix="tc-contact"
              classNames={CONTACT_FORM_CLASSES.travelClinic}
            />
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────── */}
        <FaqAccordion
          items={FAQS}
          wrapAnswerInParagraph
          classNames={{
            section: 'tc-faq',
            container: 'tc-faq__inner',
            heading: 'tc-faq__heading',
            list: 'tc-faq__list',
            item: 'tc-faq__item',
            itemOpen: 'tc-faq__item--open',
            question: 'tc-faq__question',
            questionText: 'tc-faq__q-text',
            icon: 'tc-faq__icon',
            answer: 'tc-faq__answer',
          }}
        />

        {/* ── Testimonials ─────────────────────────────────────── */}
        <div className="testimonial_sec_dyn">
          <TestimonialsSection testimonials={TESTIMONIALS} />
        </div>
      </main>

      <NewFooter />
    </>
  );
}

export default TravelClinicPage;
